<template>

    <div class="route-users">
        <div class="padding-box">

            <cms-heading class="page-heading" level="1" title="Gebruikers" icon="magicon-user">
                <cms-button color="positive" icon="magicon-plus" route="user-add" v-if="isAdmin">Gebruiker toevoegen</cms-button>
            </cms-heading>

            <cms-heading level="2" title="Alle gebruikers">
                <cms-searchbar v-model="searchQuery"></cms-searchbar>
                <cms-button color="medium-dark" type="bordered" icon="magicon-plus" @click="openAssignProjectModal" v-if="isAdmin">Toewijzen aan project</cms-button>
            </cms-heading>

            <cms-pagination class="top-pagination" :pages="pageCount" v-model="page"></cms-pagination>

            <cms-table v-spinner="loading" :item-count="totalItems" clickable="true" :items-per-page="itemsPerPage" :page="page" :offset="offset" @page="switchPage" :placeholder="users && !users.items.length">
                <cms-table-header>
                    <cms-th width="5%"><cms-checkbox :value="allVisibleUsersSelected" @input="toggleAllVisibleUsers"></cms-checkbox></cms-th>
                    <cms-th field="email" v-model="sort">E-mail</cms-th>
                    <cms-th field="firstName" v-model="sort">Voornaam</cms-th>
                    <cms-th field="lastName" v-model="sort">Achternaam</cms-th>
                    <cms-th field="role" v-model="sort">Rol</cms-th>
                    <cms-th field="lastLoginDate" v-model="sort">Laatste login</cms-th>
                    <cms-th></cms-th>
                </cms-table-header>
                <tr v-for="user in allUsers" @click="navigateUserEdit(user.id)" :class="{ selected: checkedUserIds.indexOf(user.id) >= 0 }">
                    <td @click.stop><cms-checkbox :value="checkedUserIds.indexOf(user.id) >= 0" @input="onUserToggle(user.id)"></cms-checkbox></td>
                    <td>{{ user.email }}</td>
                    <td>{{ user.firstName }}</td>
                    <td>{{ user.lastName }}</td>
                    <td>{{ user.role | userRole }}</td>
                    <td>{{ user.lastLoginDate | date('LLL') | empty }}</td>
                    <td class="actions" @click.stop>
                        <cms-link icon="img icon-table-pencil-dark" v-tooltip="'Wijzigen'" :to="{ name: 'user-edit', params: { id: user.id } }" />
                        <cms-link icon="img icon-table-cross-dark" v-tooltip="'Verwijderen'" @click="deleteUser(user)" v-if="isAdmin"/>
                    </td>
                </tr>
            </cms-table>

        </div>
    </div>

</template>

<script>

    import Component from 'vue-class-component'
    import Vue from 'global-vue'

    import createQuery from '@wittignl/js-core/apollo/queries/createQuery'
    import AllUsersQuery from 'gql/queries/Users/AllUsers.gql'

    import without from 'lodash/without'
    import intersection from 'lodash/intersection'

    import UserRole from 'app/enums/UserRole'

    import clone from 'lodash/clone'

    import UserMutations from 'app/mutations/UserMutations'

    import UserAssignProjectFormModal from 'app/components/modals/UserAssignProjectFormModal.vue'
    import UserPasswordRequestMutations from 'app/mutations/UserPasswordRequestMutations';

    @Component({

        name: 'users',
        apollo: {

            users: createQuery(AllUsersQuery, {

                fetchPolicy: 'network-only',

                skip() {

                    return !this.isAdmin
                },

                variables(){

                    return {
                        offset: this.offset,
                        limit: this.itemsPerPage,
                        search: this.searchQuery,
                        sortField: this.sort.field,
                        sortDirection: this.sort.direction
                    }
                },

                response(data) {
                    this.users = clone(data);
                }
            })
        }
    })
    export default class Users extends Vue
    {
        static SORT_STORAGE_KEY = "UsersSort";
        static SEARCH_STORAGE_KEY = "UsersSearch";

        data(){

            return {

                users: null,
                checkedUserIds: [],

                loading: 0,

                offset: 0,
                itemsPerPage: 30,
                searchQuery: this.$storage.getSession(Users.SEARCH_STORAGE_KEY, null),
                sort: this.$storage.getSession(Users.SORT_STORAGE_KEY, {
                    field: 'email',
                    direction: 'ASC'
                })
            }
        }

        breadcrumb(){

            return 'Gebruikers';
        }

        created(){

            this.logger = this.$di.logger.child('Users');

            this.$updateStore('sort', Users.SORT_STORAGE_KEY, true);
            this.$updateStore('searchQuery', Users.SEARCH_STORAGE_KEY, true);

            // Reset paging when searching
            this.$watch('searchQuery', () => {
                this.offset = 0;
            });
        }

        onQueryError(){

            this.$toast.error('De gebruikers konden niet worden opgehaald');
        }

        switchPage(offset){

            this.offset = offset;
        }

        onUserToggle(id) {

            if(this.checkedUserIds.indexOf(id) >= 0) {

                this.checkedUserIds = without(this.checkedUserIds, id);
            }
            else {

                this.checkedUserIds.push(id);
            }
        }

        toggleAllVisibleUsers(checked) {

            if(!this.users) return;

            let visibleUserIds = this.users.items.map(user => user.id);
            this.checkedUserIds = checked ? this.checkedUserIds.concat(visibleUserIds) : [];
        }

        allVisibleUsersSelected() {

            if(!this.users) return false;

            let visibleUserIds = this.users.items.map(user => user.id);

            return intersection(visibleUserIds, this.checkedUserIds).length === visibleUserIds.length && visibleUserIds.length > 0;
        }

        async openAssignProjectModal() {

            if(this.checkedUserIds.length === 0) {

                this.$toast.error('Je moet minimaal 1 gebruiker hebben geselecteerd');
                return;
            }

            const { result } = await this.$modal.show(UserAssignProjectFormModal, { userIds: this.checkedUserIds })

            if(result) {

                this.$toast.success('De gebruiker(s) is/zijn succesvol gekoppeld');

                this.checkedUserIds = [];
                this.$refetchQuery('users');
            }
        }

        async deleteUser(user){

            const { result, modal } = await this.$di.alert.confirm('Weet je zeker dat je deze gebruiker wilt verwijderen?', true);

            if(!result){
                return;
            }

            try {

                await UserMutations.delete(user.id);

                this.logger.info(`User ${user.id} deleted`);

                this.$modal.close(modal);
                this.$toast.success('De gebruiker is verwijderd');

                this.$refetchQuery('users');
            }
            catch(e){

                this.logger.error(`Error deleting user ${user.id}`, e);

                this.$modal.close(modal);
                this.$toast.error('De gebruiker kon niet worden verwijderd');
            }
        }

        get totalItems(){

            return this.users ? this.users.totalCount : 0;
        }

        get isAdmin() {

            return this.$user.role === UserRole.ADMIN
        }

        get pageCount(){

            if(!this.users){
                return 0;
            }

            return Math.ceil(parseInt(this.users.totalCount) / this.itemsPerPage);
        }

        get page(){

            return Math.floor(this.offset / parseInt(this.itemsPerPage)) + 1;
        }

        set page(page){

            this.offset = parseInt(this.itemsPerPage) * (page - 1);
        }

        get allUsers() {

            if(!this.isAdmin) {
                return [this.$user]
            }

            return this.users ? this.users.items : []
        }

        navigateUserEdit(userId) {
            this.$router.push({ name: 'user-edit', params: { id: userId } });
        }
    }

</script>
